// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import constants from "@store/constants";
import submitForm from "@store/Framework/Store/Greif/Components/SimpleQuoteForm/Handlers/SubmitForm";
import FormStepper, { FormStepperPresentationProps } from "@store/Library/Components/FormStepper/FormStepper";
import NavigationBlocker from "@store/Library/Components/NavigationBlocker";
import SimpleQuoteFormContactInformationStep from "@store/Library/Components/SimpleQuote/SimpleQuoteFormContactInformationStep";
import SimpleQuoteFormDetailsStep from "@store/Library/Components/SimpleQuote/SimpleQuoteFormDetailsStep";
import SimpleQuoteFormProductTypeStep from "@store/Library/Components/SimpleQuote/SimpleQuoteFormProductTypeStep";
import ConfirmationMessage from "@store/Library/Components/ConfirmationMessage";
import React, { FC, useContext, useEffect, useState } from "react";
import Zone from "@insite/client-framework/Components/Zone";
import useZoneIsActive from "@toolkit/Framework/Common/Hooks/useZoneIsActive";
import resetFieldValues from "@store/Framework/Store/Greif/Components/SimpleQuoteForm/Handlers/ResetFieldValues";
import {
    productTypeFormFieldFieldDefinitions,
    useParseProductTypeFormFieldFields,
} from "@store/Library/Components/FormField/ProductTypeFormField";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import translate from "@insite/client-framework/Translate";
import siteMessage from "@insite/client-framework/SiteMessage";

const formTopContentZoneName = "FormTopContent";

export interface SimpleQuoteFormProps extends WidgetProps, HasFields {}

export interface SimpleQuoteFormStyles {
    formStepper?: FormStepperPresentationProps;
}

export const simpleQuoteFormStyles: SimpleQuoteFormStyles = {};

const styles = simpleQuoteFormStyles;

const SimpleQuoteForm: FC<SimpleQuoteFormProps> = ({ fields, id }) => {
    const dispatch = useAppDispatch();
    const toasterContext = useContext(ToasterContext);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const currentBillTo = useAppSelector(getCurrentBillToState)?.value;
    const formFieldValues = useAppSelector(state => state.greif.components.simpleQuoteForm.formFieldValues);
    const { isDirty } = useAppSelector(state => state.greif.components.simpleQuoteForm);
    const formTopContentIsActive = useZoneIsActive(id, formTopContentZoneName);
    const productTypeFields = useParseProductTypeFormFieldFields(fields);

    useEffect(() => {
        return () => {
            dispatch(resetFieldValues());
        };
    }, []);

    return (
        <LoadingOverlay loading={isLoading}>
            {isSubmitted ? (
                <ConfirmationMessage body={siteMessage("SimpleQuoteForm_ConfirmationMessage")} />
            ) : (
                <>
                    {formTopContentIsActive && <Zone contentId={id} zoneName={formTopContentZoneName} />}
                    <FormStepper
                        {...styles.formStepper}
                        onSubmit={async event => {
                            event.preventDefault();

                            if (!currentBillTo) {
                                return;
                            }

                            setIsLoading(true);
                            dispatch(
                                submitForm({
                                    estimatedAnnualQuantity: formFieldValues.estimatedAnnualQuantity.value,
                                    attachments: formFieldValues.attachments.value,
                                    emailAddress: formFieldValues.emailAddress.value,
                                    firstName: formFieldValues.firstName.value,
                                    lastName: formFieldValues.lastName.value,
                                    productType: formFieldValues.productType.value,
                                    requestDetails: formFieldValues.requestDetails.value,
                                    shipToNumber: formFieldValues.shipToNumber.value,
                                    soldToNumber: currentBillTo.customerNumber,
                                    phoneNumber: formFieldValues.phoneNumber.value,
                                    referenceProductId: formFieldValues.referenceProductId.value,
                                    onSuccess: () => {
                                        setIsSubmitted(true);
                                    },
                                    onError: () => {
                                        toasterContext.addToast({
                                            body: translate("An error occurred. Try again later."),
                                            messageType: "danger",
                                        });
                                    },
                                    onComplete: () => {
                                        setIsLoading(false);
                                    },
                                }),
                            );
                        }}
                    >
                        <SimpleQuoteFormContactInformationStep />
                        <SimpleQuoteFormProductTypeStep productTypeOptions={productTypeFields.options} />
                        <SimpleQuoteFormDetailsStep />
                    </FormStepper>
                    <NavigationBlocker isActive={isDirty} />
                </>
            )}
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: SimpleQuoteForm,
    definition: {
        displayName: "Simple Quote Form",
        group: constants.widgetGroup.simpleQuote,
        fieldDefinitions: [...productTypeFormFieldFieldDefinitions],
    },
};

export default widgetModule;
